import React, { useContext, useEffect } from "react";
import { useModalDispatch, ModalForm } from "collections/Modal";
import {
  Form,
  Input,
  Field,
  FieldProps,
  StandardFormSelect,
  useField
} from "collections/Form";
import { DetailsLoader, SubmitButton } from "elements";
import { object, string, InferType } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { useCreateRoomMutation } from "generated/graphql";
import {
  Link,
  useRouteMatch,
  useHistory,
  useParams,
  generatePath
} from "polyrouter";
import useSitesAndRooms from "routes/Manage/Register/useSitesAndRooms";
import { DISMISS, useNotification } from "collections/Notifications";
import { protect } from "TsRoleGuards";
import { UNASSIGNED } from "Constants";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

// Filter for input fields of type 'number'.
// Allows only positive values to be directly entered by preventing
// '.' or '-' from appearing in input field.
// Does not prevent negative numbers generated by the number scroll, so
// an onChange handler would be needed for limiting input to positive values in that case.
// However, onChange allows for temporary appearance of directly entered '.' or '-',
// so the need for this filter.
export const positiveNumberFilter = (
  event: React.KeyboardEvent<HTMLInputElement>
) => {
  if (event.code === "Period" || event.code === "Minus") {
    if (event.preventDefault) {
      event.preventDefault();
    }
  }
};

const NewRoomSchema = object({
  name: string()
    .label("NAME")
    .test(
      UNASSIGNED,
      i18n.t("ERROR_UNASSIGNED_ROOM_NAME"),
      (value: string) => !nameIsUnassigned(value)
    )
    .required()
    .ensure(),
  site: string()
    .label("SITE")
    .required(),
  floor: string()
    .label("FLOOR")
    .notRequired(),
  capacity: string()
    .matches(/^\d+$/)
    .label("CAPACITY")
    .notRequired()
});
export type RoomType = InferType<typeof NewRoomSchema>;

const SyncSite = () => {
  const [field, , helpers] = useField("site");
  const { sites, siteMeta } = useSitesAndRooms();
  const { siteId } = useParams<{ siteId: string | undefined }>();

  const site = (siteMeta?.sites || []).filter(
    (site: any) => site.id === siteId
  )[0];

  useEffect(() => {
    if (siteId && sites.length && site.name !== field.value) {
      helpers.setValue(site.name);
    }
  });
  return null;
};

const Sites: React.FC<{ replaceAddress: boolean }> = ({ replaceAddress }) => {
  const { t } = useTranslation();
  let { sites } = useSitesAndRooms();
  const [, , helpers] = useField("site");
  const { replace } = useHistory();

  sites = (sites || []).filter(site => !nameIsUnassigned(site.text));

  const setSiteParam = (id: string) => {
    if (!id) return;

    if (replaceAddress) {
      replace(
        generatePath("/manage/sites/details/:siteId/add", { siteId: id })
      );
    } else {
      helpers.setValue(id);
    }
  };
  return (
    <StandardFormSelect
      label={t("SITE_NAME")}
      name="site"
      options={sites}
      tabIndex={2}
      onSelectionChange={id => setSiteParam(id)}
      inline={true}
    />
  );
};

const CreateRoomModal: React.FC<{
  refresh: () => void;
  withSites?: boolean;
}> = ({ refresh, withSites = true }) => {
  const { t } = useTranslation();
  const { url } = useRouteMatch();
  const cancelURL = url.replace("/add", "");
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const [, createRoom] = useCreateRoomMutation();
  const { siteId } = useParams<{ siteId: string | undefined }>();
  const dismissModal = useModalDispatch();
  const [addNotification] = useNotification();
  const { push } = useHistory();

  const initialValues = {
    name: "",
    site: "",
    floor: "",
    capacity: ""
  };

  const onSubmit = async (values: RoomType) => {
    const roomResponse = await createRoom({
      fields: {
        tenantId: tenantId,
        siteId: siteId || values.site,
        name: values.name,
        floor: values.floor,
        // Sets capacity to -1 if field is empty. Prospector service clears
        // capacity if it receives -1 and leaves unchanged if it receives null.
        capacity: !values.capacity ? -1 : parseInt(values.capacity)
      }
    });

    if (roomResponse.data?.upsertRoom) {
      refresh();
      if (withSites) {
        push(
          generatePath("/manage/sites/details/:siteId", {
            siteId: siteId || values.site
          })
        );
      } else {
        dismissModal();
      }
    } else if (roomResponse.error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: "Error creating room: " + roomResponse.error.message
      });
    }
  };

  return (
    <ModalForm
      title={t("ADD_ROOM")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"CreateRoomModal"}
      validationSchema={NewRoomSchema}
      validateOnChange={true}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form>
          <DetailsLoader loading={isSubmitting} title={t("SAVING_ROOM")}>
            {withSites && <SyncSite />}
            <div className="form-content">
              <Input name="name" label={t("ROOM_NAME")} tabIndex={1} />
              <Sites replaceAddress={withSites} />
              <Input name="floor" label={t("FLOOR_NAME")} tabIndex={3} />
              <Input
                name="capacity"
                label={t("CAPACITY_OPTIONAL")}
                tabIndex={4}
                type="number"
                onKeyPress={positiveNumberFilter}
                onChange={e => {
                  if (Number(e.target.value) >= 0) {
                    setFieldValue("capacity", e.target.value);
                  }
                }}
              />
            </div>
          </DetailsLoader>
          <div className="btn-container">
            <Link to={cancelURL} className="btn" data-testid="close">
              {t("CANCEL")}
            </Link>
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  isSubmitting={isSubmitting}
                  testId="save"
                  disabled={!isValid || !dirty}
                  labelKey="ADD_ROOM"
                  submittingLabelKey="ADDING"
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};

export default protect("UPDATE_SITES")(CreateRoomModal);
